import { isVoterStateFilterValue, VOTER_SEARCH_MAX_LENGTH } from './voterPresentation';
import { DEFAULT_VOTER_PAGE, DEFAULT_VOTER_PAGE_SIZE, isVoterPageSize, type VotersListState } from './voterListState';

export type StoredVotersListState = VotersListState & {
  accountScope: string;
};

export type VoterReturnTarget = {
  to: string;
  state?: { votersList: StoredVotersListState };
};

const VOTER_DETAIL_PATH_PATTERN = /^\/voters\/[1-9]\d{0,18}$/;
const POSTGRES_BIGINT_MAX = 9223372036854775807n;

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

const readProperty = (value: unknown, key: string): unknown => (isRecord(value) ? Reflect.get(value, key) : undefined);

export const isSafeVoterDetailPath = (value: unknown): value is string => {
  if (typeof value !== 'string') return false;
  if (
    value.startsWith('//') ||
    value.includes('\\') ||
    value.includes('://') ||
    value.includes('?') ||
    value.includes('#') ||
    value.includes('%')
  ) {
    return false;
  }
  if (!VOTER_DETAIL_PATH_PATTERN.test(value)) return false;

  try {
    const id = BigInt(value.slice('/voters/'.length));
    return id > 0n && id <= POSTGRES_BIGINT_MAX;
  } catch {
    return false;
  }
};

export const buildVoterDetailPath = (id: unknown): string | null => {
  if (typeof id !== 'string' || id.trim() !== id) return null;
  const path = `/voters/${id}`;
  return isSafeVoterDetailPath(path) ? path : null;
};

const isStoredVotersListState = (value: unknown): value is StoredVotersListState => {
  if (!isRecord(value)) return false;
  const search = Reflect.get(value, 'search');
  const state = Reflect.get(value, 'state');
  const page = Reflect.get(value, 'page');
  const rowsPerPage = Reflect.get(value, 'rowsPerPage');
  const accountScope = Reflect.get(value, 'accountScope');

  return (
    typeof search === 'string' &&
    search.length <= VOTER_SEARCH_MAX_LENGTH &&
    search.trim() === search &&
    (state === undefined || isVoterStateFilterValue(state)) &&
    typeof page === 'number' &&
    Number.isSafeInteger(page) &&
    page >= 0 &&
    isVoterPageSize(rowsPerPage) &&
    typeof accountScope === 'string' &&
    accountScope.length > 0 &&
    accountScope.length <= 2_000
  );
};

export const toStoredVotersListState = (listState: VotersListState, accountScope: string): StoredVotersListState => ({
  search: listState.search,
  state: listState.state,
  page: listState.page,
  rowsPerPage: listState.rowsPerPage,
  accountScope,
});

export const readVotersListState = (locationState: unknown, accountScope: string): VotersListState | null => {
  const stored = readProperty(locationState, 'votersList');
  if (!isStoredVotersListState(stored) || stored.accountScope !== accountScope) return null;

  return {
    search: stored.search,
    state: typeof stored.state === 'string' ? stored.state : '',
    page: stored.page,
    rowsPerPage: stored.rowsPerPage,
  };
};

export const resolveVoterReturn = (locationState: unknown): VoterReturnTarget | null => {
  const raw = readProperty(locationState, 'voterReturn');
  const to = readProperty(raw, 'to');
  if (!isSafeVoterDetailPath(to)) return null;

  const nested = readProperty(raw, 'state');
  const votersList = readProperty(nested, 'votersList');
  if (!isStoredVotersListState(votersList)) return { to };

  return { to, state: { votersList } };
};

export const buildDoorVisitLocationState = (
  voterId: string,
  listState: VotersListState | null,
  accountScope: string,
): { voterReturn: VoterReturnTarget } | undefined => {
  const to = buildVoterDetailPath(voterId);
  if (to === null) return undefined;
  if (listState === null) return { voterReturn: { to } };

  return {
    voterReturn: {
      to,
      state: { votersList: toStoredVotersListState(listState, accountScope) },
    },
  };
};

export const defaultVotersListState = (): VotersListState => ({
  search: '',
  state: '',
  page: DEFAULT_VOTER_PAGE,
  rowsPerPage: DEFAULT_VOTER_PAGE_SIZE,
});
